import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormControlLabel,
  Switch,
  Checkbox,
  Typography,
  Divider,
  Box,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { Device, DeviceType, InferenceModel, InferenceModelClass, InferenceNode } from './types';

interface BusinessOption {
  id: string;
  name: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved: (device: Device) => void;
  initial?: Device | null;
  defaultBusinessId?: string | null;
};

const defaultState: Device = { name: '', deviceTypeId: '', parentId: null, inferenceModelId: null, inferenceNodeId: null, inferenceEnabled: true, inferenceConfidence: null, inferenceClasses: null };

const DeviceFormDialog: React.FC<Props> = ({ open, onClose, onSaved, initial, defaultBusinessId }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const [model, setModel] = useState<Device>(defaultState);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [inferenceModels, setInferenceModels] = useState<InferenceModel[]>([]);
  const [inferenceNodes, setInferenceNodes] = useState<InferenceNode[]>([]);
  const [businesses, setBusinesses] = useState<BusinessOption[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');
  // null = unrestricted / not yet known — don't filter the dropdown
  const [allowedModelIds, setAllowedModelIds] = useState<Set<string> | null>(null);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confidenceText, setConfidenceText] = useState('');
  // Selected class indices for the assigned InferenceModel (null = all classes)
  const [selectedClasses, setSelectedClasses] = useState<Set<number> | null>(null);

  // Load device types and devices
  useEffect(() => {
    const loadDeviceTypes = async () => {
      setLoadingTypes(true);
      try {
        const res = await fetch('/api/device-types', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setDeviceTypes(data);
        }
      } catch (e) {
        console.error('Failed to load device types:', e);
      } finally {
        setLoadingTypes(false);
      }
    };

    const loadDevices = async () => {
      setLoadingDevices(true);
      try {
        const res = await fetch('/api/devices', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          // Filter out current device if editing (can't be parent of itself), then sort alphabetically
          const filtered = data.filter((d: Device) => !initial || d.id !== initial.id);
          filtered.sort((a: Device, b: Device) => (a.name ?? '').localeCompare(b.name ?? ''));
          setDevices(filtered);
        }
      } catch (e) {
        console.error('Failed to load devices:', e);
      } finally {
        setLoadingDevices(false);
      }
    };

    const loadInferenceModels = async () => {
      try {
        const res = await fetch('/api/inference-models');
        if (res.ok) {
          const data = await res.json();
          setInferenceModels(data);
        }
      } catch (e) {
        console.error('Failed to load inference models:', e);
      }
    };

    const loadInferenceNodes = async () => {
      try {
        const res = await fetch('/api/inference-nodes');
        if (res.ok) {
          const data = await res.json();
          setInferenceNodes(data);
        }
      } catch (e) {
        console.error('Failed to load inference nodes:', e);
      }
    };

    const loadBusinesses = async () => {
      try {
        const res = await fetch('/api/businesses/list', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          setBusinesses(data);
        }
      } catch (e) {
        console.error('Failed to load businesses:', e);
      }
    };

    if (open) {
      loadDeviceTypes();
      loadDevices();
      loadInferenceModels();
      loadInferenceNodes();
      loadBusinesses();
    }
  }, [open, accessToken, initial]);

  useEffect(() => {
    if (initial) {
      setModel({
        id: initial.id,
        name: initial.name ?? '',
        deviceTypeId: initial.deviceTypeId ?? '',
        parentId: initial.parentId ?? null,
        inferenceModelId: initial.inferenceModelId ?? null,
        inferenceNodeId: initial.inferenceNodeId ?? null,
        inferenceEnabled: initial.inferenceEnabled ?? true,
        inferenceConfidence: initial.inferenceConfidence ?? null,
        inferenceClasses: initial.inferenceClasses ?? null,
        dateCreated: initial.dateCreated,
        dateArchived: initial.dateArchived ?? null,
      });
      setConfidenceText(initial.inferenceConfidence != null ? String(initial.inferenceConfidence) : '');
      if (initial.inferenceClasses) {
        const indices = initial.inferenceClasses.split(',').map(Number).filter(n => !isNaN(n));
        setSelectedClasses(new Set(indices));
      } else {
        setSelectedClasses(null);
      }
    } else {
      setModel(defaultState);
      setConfidenceText('');
      setSelectedClasses(null);
    }
    // Business association is only set at creation time via this form — pre-fill
    // from whatever business context the Devices page was showing when "Add
    // Device" was clicked (selected filter, falling back to the user's default).
    setSelectedBusinessId(initial ? '' : (defaultBusinessId ?? ''));
  }, [initial, open, defaultBusinessId]);

  // Filter the Inference Model dropdown to what the relevant business's plan
  // allows. Falls back to defaultBusinessId when editing (no business picker
  // shown then) — best-effort UX only, the API is the actual enforcement point.
  useEffect(() => {
    const effectiveBusinessId = selectedBusinessId || defaultBusinessId || '';
    if (!open || !effectiveBusinessId) {
      setAllowedModelIds(null);
      return;
    }
    const loadAllowedModels = async () => {
      try {
        const res = await fetch(`/api/businesses/${effectiveBusinessId}/allowed-inference-models`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.ok) {
          const data: InferenceModel[] = await res.json();
          setAllowedModelIds(new Set(data.map((m) => m.id)));
        } else {
          setAllowedModelIds(null);
        }
      } catch (e) {
        console.error('Failed to load allowed inference models:', e);
        setAllowedModelIds(null);
      }
    };
    loadAllowedModels();
  }, [open, accessToken, selectedBusinessId, defaultBusinessId]);

  const selectedModelClasses = (): InferenceModelClass[] => {
    if (!model.inferenceModelId) return [];
    return inferenceModels.find(m => m.id === model.inferenceModelId)?.classes ?? [];
  };

  const handleClassToggle = (classIndex: number) => {
    const classes = selectedModelClasses();
    setSelectedClasses(prev => {
      const current = prev ?? new Set(classes.map(c => c.classIndex));
      const next = new Set(current);
      if (next.has(classIndex)) {
        next.delete(classIndex);
      } else {
        next.add(classIndex);
      }
      // If all classes selected, store null (= all)
      if (next.size === classes.length) return null;
      return next;
    });
  };

  const classesToCsv = (): string | null => {
    const classes = selectedModelClasses();
    if (!selectedClasses || selectedClasses.size === classes.length) return null;
    return Array.from(selectedClasses).sort((a, b) => a - b).join(',');
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: Device = {
        id: model.id,
        name: model.name,
        deviceTypeId: model.deviceTypeId,
        parentId: model.parentId || null,
        inferenceModelId: model.inferenceModelId || null,
        inferenceNodeId: model.inferenceNodeId || null,
        inferenceEnabled: model.inferenceEnabled ?? true,
        inferenceConfidence: model.inferenceConfidence ?? null,
        inferenceClasses: model.inferenceModelId ? classesToCsv() : null,
      };
      const isEdit = Boolean(body.id);
      const url = isEdit ? `/api/devices/${body.id}` : '/api/devices';
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        let message = text || 'Save failed';
        try {
          const parsed = JSON.parse(text);
          if (parsed?.error) message = parsed.error;
        } catch {
          // Not JSON — use the raw text as-is.
        }
        throw new Error(message);
      }
      const saved = method === 'POST' ? await res.json() : body;

      if (!isEdit && selectedBusinessId && saved.id) {
        try {
          await fetch(`/api/businesses/${selectedBusinessId}/devices`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ deviceId: saved.id }),
          });
        } catch (linkErr) {
          // Device itself was created fine — a failed link just means it'll
          // need linking manually later via the business's Details page.
          console.error('Failed to link new device to business:', linkErr);
        }
      }

      onSaved(saved);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{model.id ? 'Edit Device' : 'Add Device'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name"
            value={model.name}
            onChange={(e) => setModel((m) => ({ ...m, name: e.target.value }))}
            autoFocus
            required
            fullWidth
          />
          <FormControl fullWidth required>
            <InputLabel id="device-type-label">Device Type</InputLabel>
            <Select
              labelId="device-type-label"
              label="Device Type"
              value={model.deviceTypeId}
              onChange={(e) => setModel((m) => ({ ...m, deviceTypeId: e.target.value }))}
              disabled={loadingTypes}
            >
              {loadingTypes && (
                <MenuItem disabled>
                  <CircularProgress size={20} /> Loading...
                </MenuItem>
              )}
              {deviceTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!model.id && (
            <FormControl fullWidth>
              <InputLabel id="business-label">Business</InputLabel>
              <Select
                labelId="business-label"
                label="Business"
                value={selectedBusinessId}
                onChange={(e) => setSelectedBusinessId(e.target.value)}
              >
                <MenuItem value="">
                  <em>None (link later)</em>
                </MenuItem>
                {businesses.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth>
            <InputLabel id="parent-device-label">Parent Device (Optional)</InputLabel>
            <Select
              labelId="parent-device-label"
              label="Parent Device (Optional)"
              value={model.parentId || ''}
              onChange={(e) => setModel((m) => ({ ...m, parentId: e.target.value || null }))}
              disabled={loadingDevices}
            >
              <MenuItem value="">
                <em>None (Top-level device)</em>
              </MenuItem>
              {loadingDevices && (
                <MenuItem disabled>
                  <CircularProgress size={20} /> Loading...
                </MenuItem>
              )}
              {devices.map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  {device.name} ({device.deviceType?.name || 'Unknown Type'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="inference-model-label">Inference Model (Optional)</InputLabel>
            <Select
              labelId="inference-model-label"
              label="Inference Model (Optional)"
              value={model.inferenceModelId || ''}
              onChange={(e) => {
                setModel((m) => ({ ...m, inferenceModelId: e.target.value || null }));
                setSelectedClasses(null); // reset to "all classes" when model changes
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {inferenceModels
                .filter((im) => !allowedModelIds || allowedModelIds.has(im.id) || im.id === model.inferenceModelId)
                .map((im) => (
                  <MenuItem key={im.id} value={im.id}>
                    {im.displayName}
                  </MenuItem>
                ))}
            </Select>
            {allowedModelIds && inferenceModels.some((im) => !allowedModelIds.has(im.id)) && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                Filtered to this business's current plan.
              </Typography>
            )}
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="inference-node-label">Inference Node (Optional)</InputLabel>
            <Select
              labelId="inference-node-label"
              label="Inference Node (Optional)"
              value={model.inferenceNodeId || ''}
              onChange={(e) => setModel((m) => ({ ...m, inferenceNodeId: e.target.value || null }))}
            >
              <MenuItem value="">
                <em>None (default inference host)</em>
              </MenuItem>
              {inferenceNodes.map((n) => (
                <MenuItem key={n.id} value={n.id}>
                  {n.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Class selection — shown when an InferenceModel with classes is assigned */}
          {model.inferenceModelId && selectedModelClasses().length > 0 && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Detection classes (uncheck to exclude)
              </Typography>
              {selectedModelClasses().map((cls) => {
                const isChecked = !selectedClasses || selectedClasses.has(cls.classIndex);
                return (
                  <FormControlLabel
                    key={cls.classIndex}
                    control={
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        onChange={() => handleClassToggle(cls.classIndex)}
                      />
                    }
                    label={`${cls.className} (class ${cls.classIndex})`}
                    sx={{ display: 'block', ml: 0 }}
                  />
                );
              })}
            </Box>
          )}

          <Divider />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>Inference Settings</Typography>

          <FormControlLabel
            control={
              <Switch
                checked={model.inferenceEnabled ?? true}
                onChange={(e) => setModel((m) => ({ ...m, inferenceEnabled: e.target.checked }))}
              />
            }
            label="Inference enabled"
          />

          <TextField
            label="Confidence threshold"
            type="number"
            size="small"
            value={confidenceText}
            onChange={(e) => setConfidenceText(e.target.value)}
            onBlur={() => {
              if (confidenceText === '') {
                setModel((m) => ({ ...m, inferenceConfidence: null }));
              } else {
                const v = parseFloat(confidenceText);
                const clamped = isNaN(v) ? null : Math.min(1, Math.max(0.1, v));
                setModel((m) => ({ ...m, inferenceConfidence: clamped }));
                setConfidenceText(clamped != null ? String(clamped) : '');
              }
            }}
            inputProps={{ min: 0.1, max: 1.0, step: 0.05 }}
            helperText="0.10 – 1.00 · leave blank to use device default"
            fullWidth
            disabled={!(model.inferenceEnabled ?? true)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving || !model.name.trim() || !model.deviceTypeId}
        >
          {model.id ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceFormDialog;
